import React from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  DriverSelectOption,
  NO_DRIVER_OPTION_ID,
} from "./buildDriverSelectOptions";
import * as Styled from "./styles";

function isDriverOnDelivery(option: DriverSelectOption): boolean {
  return (option.activeOrders ?? 0) > 0 || option.enabled === false;
}

function resolveDotVariant(option: DriverSelectOption): Styled.DotVariant {
  if (option.groupKey === "none") {
    return "neutral";
  }
  if (isDriverOnDelivery(option)) {
    return "busy";
  }
  return "available";
}

function buildDriverDisplayLabel(
  option: DriverSelectOption,
  availableLabel: string,
  onDeliveryLabel: string,
): string {
  if (option.groupKey === "none") {
    return option.label;
  }

  const statusLabel = isDriverOnDelivery(option)
    ? onDeliveryLabel
    : availableLabel;

  return `${option.label} - ${statusLabel}`;
}

function renderStatusDot(
  option: DriverSelectOption,
  showDriverInsight: boolean,
): React.ReactNode {
  if (!showDriverInsight) {
    return null;
  }

  const isNoDriverOption =
    option.id === NO_DRIVER_OPTION_ID || option.groupKey === "none";

  if (isNoDriverOption) {
    return <Styled.StatusDot $variant="neutral" />;
  }

  return <Styled.StatusDot $variant={resolveDotVariant(option)} />;
}

interface DriverOptionRowProps {
  liProps: React.HTMLAttributes<HTMLLIElement>;
  option: DriverSelectOption;
  showDriverInsight: boolean;
}

function DriverOptionRow({
  liProps,
  option,
  showDriverInsight,
}: DriverOptionRowProps) {
  const { t } = useTranslation();
  const isNoDriverOption =
    option.id === NO_DRIVER_OPTION_ID || option.groupKey === "none";
  const shouldShowInsight = showDriverInsight && !isNoDriverOption;
  const activeOrders = option.activeOrders ?? 0;
  const showPendingOrders = shouldShowInsight && activeOrders > 0;
  const displayLabel = shouldShowInsight
    ? buildDriverDisplayLabel(
        option,
        t("driverDropdownStatusAvailable"),
        t("driverDropdownStatusOnDelivery"),
      )
    : option.label;

  return (
    <Box
      component="li"
      {...liProps}
      key={option.id}
      sx={{ padding: "0 !important", display: "block" }}
    >
      <Styled.OptionRow>
        {renderStatusDot(option, showDriverInsight)}
        <Styled.DriverOptionContent>
          <Styled.DriverOptionName title={displayLabel}>
            {displayLabel}
          </Styled.DriverOptionName>
          {showPendingOrders && (
            <Styled.DriverPendingOrdersText>
              {t("driverPendingOrdersCount", { count: activeOrders })}
            </Styled.DriverPendingOrdersText>
          )}
        </Styled.DriverOptionContent>
      </Styled.OptionRow>
    </Box>
  );
}

export default DriverOptionRow;
